import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { PublicJobDetail } from '@absurd-careers/shared';
import { PageMeta } from '~/components/PageMeta.js';
import { ButtonLink } from '~/components/ui/Button.js';
import { ErrorState, Loading } from '~/components/ui/Feedback.js';
import { Prose } from '~/components/ui/Prose.js';
import { SITE } from '~/content/site.js';
import { ApiRequestError } from '~/lib/api.js';
import { formatCompensation, formatDate, formatEmploymentType, formatWorkplaceType } from '~/lib/format.js';
import { buildJobPostingJsonLd } from '~/lib/job-posting.js';
import { usePublicJob } from '~/lib/queries.js';

function Facts({ job }: { job: PublicJobDetail }) {
  const compensation = formatCompensation(job.compensation);
  const facts: { label: string; value: string }[] = [
    { label: 'Department', value: job.department },
    { label: 'Location', value: job.locationText },
    { label: 'Workplace', value: formatWorkplaceType(job.workplaceType) },
    { label: 'Employment', value: formatEmploymentType(job.employmentType) },
  ];
  if (compensation) facts.push({ label: 'Compensation', value: compensation });
  if (job.closesAt) facts.push({ label: 'Applications close', value: formatDate(job.closesAt) });

  return (
    <dl className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-1">
      {facts.map((fact) => (
        <div key={fact.label} className="border-t border-line pt-4">
          <dt className="section-label">{fact.label}</dt>
          <dd className="mt-1.5 text-sm text-body">{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ShareLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard
            ?.writeText(url)
            .then(() => setCopied(true))
            .catch(() => setCopied(false));
        }}
        className="border-b border-line-strong pb-0.5 text-sm text-muted transition-colors hover:border-primary hover:text-primary"
      >
        Copy link to this role
      </button>
      <span role="status" aria-live="polite" className="text-xs text-primary">
        {copied ? 'Link copied.' : ''}
      </span>
    </div>
  );
}

function LongSection({ title, html }: { title: string; html: string | null }) {
  if (!html) return null;
  return (
    <section className="mt-12 border-t border-line pt-8">
      <h2 className="display-3">{title}</h2>
      <Prose html={html} className="mt-4" />
    </section>
  );
}

export function JobDetailPage() {
  const { slug = '' } = useParams();
  const { data: job, isPending, error } = usePublicJob(slug);

  if (isPending) {
    return (
      <div className="mx-auto max-w-(--container-content) px-5 py-16 sm:px-8">
        <Loading label="Loading role" />
      </div>
    );
  }

  if (error) {
    const notFound = error instanceof ApiRequestError && error.status === 404;
    return (
      <div className="mx-auto max-w-(--container-content) px-5 py-24 sm:px-8">
        <PageMeta
          title={notFound ? 'Role not found' : 'Something went wrong'}
          description={SITE.defaultDescription}
          path={'/jobs/' + slug}
          noIndex
        />
        <ErrorState heading={notFound ? 'That role is not open.' : 'We could not load this role.'}>
          <p>
            {notFound
              ? 'It may have been filled, or the link may be out of date.'
              : 'This is a problem on our side. Please try again shortly.'}
          </p>
          <Link to="/#open-roles" className="mt-4 inline-block text-primary underline underline-offset-4">
            See the roles that are open
          </Link>
        </ErrorState>
      </div>
    );
  }

  const canonicalPath = '/jobs/' + job.slug;
  const shareUrl = SITE.careersUrl + canonicalPath;
  const closed = !job.acceptingApplications;

  return (
    <>
      <PageMeta
        title={job.title}
        description={job.summary}
        path={canonicalPath}
        noIndex={closed}
        jsonLd={buildJobPostingJsonLd(job, shareUrl)}
      />

      <article className="mx-auto max-w-(--container-content) px-5 pt-12 pb-4 sm:px-8 sm:pt-16">
        <Link to="/#open-roles" className="text-sm text-muted hover:text-primary">
          ← All open roles
        </Link>

        <div className="mt-6 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-8">
            <p className="section-label">{job.department}</p>
            <h1 className="mt-3 display-1 text-[2.25rem] sm:text-5xl lg:text-6xl">{job.title}</h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted">{job.summary}</p>

            {closed ? (
              <div
                role="status"
                className="mt-8 rounded-lg border border-line-strong bg-surface px-4 py-3 text-sm text-body"
              >
                <p className="font-medium text-heading">This role is closed.</p>
                <p className="mt-1">
                  We are no longer accepting applications for it. Applications already submitted are still
                  with us.
                </p>
              </div>
            ) : (
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <ButtonLink to={canonicalPath + '/apply'}>Apply for this role</ButtonLink>
                <ShareLink url={shareUrl} />
              </div>
            )}

            <div className="mt-12">
              <Prose html={job.descriptionHtml} />
            </div>

            <LongSection title="What you will do" html={job.responsibilitiesHtml} />
            <LongSection title="What the role needs" html={job.requiredQualificationsHtml} />
            <LongSection title="Also useful" html={job.preferredQualificationsHtml} />
            <LongSection title="How hiring works for this role" html={job.hiringProcessNoteHtml} />

            {closed ? null : (
              <div className="mt-14 flex flex-wrap items-center gap-4 border-t border-line pt-8">
                <ButtonLink to={canonicalPath + '/apply'}>Apply for this role</ButtonLink>
                <ShareLink url={shareUrl} />
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <Facts job={job} />
              {closed ? null : (
                <ButtonLink to={canonicalPath + '/apply'} className="mt-8 w-full">
                  Apply for this role
                </ButtonLink>
              )}
            </div>
          </aside>
        </div>
      </article>
    </>
  );
}
